import React, { useContext } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { RESOURCE_NAME } from "common/authorization";
import {
  OBJECT_TYPE,
  WORKFLOW_STEP_MANUAL_STATUS,
  WORKFLOW_STEP_ROLE,
} from "common/constants";
import { buildLink } from "common/routing";
import { GetWorkflowQuery } from "../../generated/graphql";
import { AuthContext } from "../Authorization/AuthContext";
import { Button } from "../Common/Buttons/Button";

type WorkflowStep = Omit<
  NonNullable<GetWorkflowQuery["workflow"]>["workflowSteps"][number],
  "status"
>;

const isManualStatusEnabledForPublicUser = (
  manualStatus: Maybe<WORKFLOW_STEP_MANUAL_STATUS>,
): boolean => {
  if (!manualStatus) {
    return true;
  }

  switch (manualStatus) {
    case WORKFLOW_STEP_MANUAL_STATUS.NOT_STARTED:
    case WORKFLOW_STEP_MANUAL_STATUS.NOT_APPLICABLE:
      return false;
    case WORKFLOW_STEP_MANUAL_STATUS.IN_PROGRESS:
    case WORKFLOW_STEP_MANUAL_STATUS.COMPLETE:
    case WORKFLOW_STEP_MANUAL_STATUS.REJECTED:
      return true;
  }
};

const shouldForceViewOnly = (
  manualStatus: Maybe<WORKFLOW_STEP_MANUAL_STATUS>,
): boolean => {
  if (!manualStatus) {
    return false;
  }

  return (
    manualStatus === WORKFLOW_STEP_MANUAL_STATUS.COMPLETE ||
    manualStatus === WORKFLOW_STEP_MANUAL_STATUS.REJECTED
  );
};

const getCtaText = (
  workflowStep: WorkflowStep,
  canCreateSubmission: boolean,
  isManualMode: boolean,
  isPublic: boolean,
): string => {
  // In manual mode, force "View" for complete/rejected statuses
  if (
    isManualMode &&
    isPublic &&
    shouldForceViewOnly(workflowStep.manualStatus)
  ) {
    return "View";
  }

  switch (workflowStep.workflowStepUser?.stepRole) {
    case WORKFLOW_STEP_ROLE.CREATE:
      return workflowStep.submission ? "View" : "Create";
    case WORKFLOW_STEP_ROLE.UPDATE:
      return "Review";
    case WORKFLOW_STEP_ROLE.VIEW:
      return "View";
    case undefined:
      return canCreateSubmission && !workflowStep.submission ?
          "Create"
        : "View";
  }
};

const getOnCallToActionClick = ({
  canCreateSubmission,
  workflowStep,
  push,
  pathname,
  objectId,
  objectType,
  isManualMode,
  isPublic,
}: {
  canCreateSubmission: boolean;
  workflowStep: WorkflowStep;
  push: ReturnType<typeof useHistory>["push"];
  pathname: string;
  objectId?: string;
  objectType?: OBJECT_TYPE;
  isManualMode: boolean;
  isPublic: boolean;
}): (() => void) | undefined => {
  // In manual mode for public users, check manual status
  if (
    isManualMode &&
    isPublic &&
    !isManualStatusEnabledForPublicUser(workflowStep.manualStatus)
  ) {
    return undefined;
  }

  // Force view-only for complete/rejected in manual mode
  const forceViewOnly =
    isManualMode && isPublic && shouldForceViewOnly(workflowStep.manualStatus);

  if (canCreateSubmission && !workflowStep.submission && !forceViewOnly) {
    return () =>
      push({
        pathname: buildLink("createSubmission", {
          submissionTypeId:
            workflowStep.workflowStepDefinition.submissionType.id,
          objectId: objectId ?? undefined,
          objectType: objectType ?? undefined,
        }),
        state: { prevLocation: pathname, workflowStepId: workflowStep.id },
      });
  }

  if (workflowStep.submission) {
    return () =>
      push({
        pathname: buildLink("editSubmission", {
          submissionId: workflowStep.submission!.id,
        }),
        state: { prevLocation: pathname },
      });
  }

  return undefined;
};

export const WorkflowStepCTA = ({
  workflowStep,
  objectId,
  objectType,
  isManualMode,
}: {
  workflowStep: WorkflowStep;
  objectId?: string;
  objectType?: OBJECT_TYPE;
  isManualMode: boolean;
}) => {
  const history = useHistory();
  const { pathname } = useLocation();
  const { isPublic, authorized } = useContext(AuthContext);

  const canCreateSubmission =
    isPublic ?
      workflowStep.workflowStepUser?.stepRole === WORKFLOW_STEP_ROLE.CREATE
    : authorized({
        permission: "create",
        resource: RESOURCE_NAME.SUBMISSION,
      });

  const onCallToActionClick = getOnCallToActionClick({
    canCreateSubmission,
    workflowStep,
    push: history.push,
    pathname,
    objectId,
    objectType,
    isManualMode,
    isPublic,
  });

  const ctaText = getCtaText(
    workflowStep,
    canCreateSubmission,
    isManualMode,
    isPublic,
  );

  if (!isManualMode) {
    return (
      <Button
        variant="outline"
        size="small"
        onPress={onCallToActionClick}
        isDisabled={!onCallToActionClick}
        data-testid="workflow-step-cta"
        title={ctaText}
      />
    );
  }

  return onCallToActionClick || !isPublic ?
      <Button
        variant="outline"
        size="small"
        onPress={onCallToActionClick}
        isDisabled={!onCallToActionClick}
        data-testid="workflow-step-cta"
        title={ctaText}
      />
    : null;
};
